import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Grid,
  List, ListItem, ListItemButton, ListItemText, Avatar, Collapse, Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { getHansard, getHansardToc, getParlViewVideo } from '../services/api';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';
import ReportIssueButton from './ReportIssueButton';
import DownloadPdfButton from './DownloadPdfButton';
import DownloadXmlButton from './DownloadXmlButton';
import { getParliamentarianPhotoUrl } from '../utils/photos';

// Helper functions for navigation - handle both modern and historic field names
function getPrevId(fragment) {
  return fragment?.prev_fragment_id || fragment?.prev_id || null;
}

function getNextId(fragment) {
  return fragment?.next_fragment_id || fragment?.next_id || null;
}

// Helper function to collect all fragment IDs from an entry and its children
function collectFragmentIds(entry) {
  const ids = [];
  if (entry.fragment_id) {
    ids.push(entry.fragment_id);
  }
  if (entry.children && entry.children.length > 0) {
    entry.children.forEach(child => {
      ids.push(...collectFragmentIds(child));
    });
  }
  return ids;
}

// Recursive component for rendering TOC entries
function TocEntry({ entry, currentId, loadedIds, level = 0, onSelectFragment }) {
  const [open, setOpen] = useState(true);
  const hasChildren = entry.children && entry.children.length > 0;
  const isCurrentItem = entry.fragment_id === currentId;
  const isLoaded = loadedIds.includes(entry.fragment_id);

  const handleClick = () => {
    // #160/#161: clicking a TOC node navigates to that fragment's own URL, so the
    // selection is shareable + traversable via browser Back/Forward, and the
    // landing fragment is rendered first and scrolled into view (handled by the
    // id-driven load effect below). Some parent nodes have no fragment_id of their
    // own (their children carry the fragments) — fall back to the first descendant
    // fragment so the click still does something useful.
    let targetId = entry.fragment_id;
    if (!targetId) {
      const ids = collectFragmentIds(entry);
      targetId = ids.length > 0 ? ids[0] : null;
    }
    if (targetId) {
      onSelectFragment(targetId);
    }
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  // Determine styling based on entry type
  const getEntryStyle = () => {
    if (entry.type === 'debate') {
      return { fontWeight: 600, fontSize: '0.9rem' };
    }
    if (entry.type === 'subdebate') {
      return { fontWeight: 500, fontSize: '0.85rem' };
    }
    return { fontSize: '0.8rem' };
  };

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleClick}
          selected={isCurrentItem}
          sx={{
            pl: 1 + level * 2,
            py: 0.5,
            borderLeft: '3px solid',
            borderLeftColor: isCurrentItem ? '#c62828' : isLoaded ? 'divider' : 'transparent',
            '&.Mui-selected': {
              bgcolor: 'rgba(198, 40, 40, 0.08)'
            },
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
        >
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  ...getEntryStyle(),
                  color: isCurrentItem ? '#c62828' : isLoaded ? '#666' : 'text.primary'
                }}
              >
                {entry.title}
                {hasChildren && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({entry.children.length})
                  </Typography>
                )}
              </Typography>
            }
            secondary={
              entry.speaker && entry.type === 'speech' && (
                <Typography variant="caption" color="text.secondary">
                  {entry.party && `${entry.party} • `}
                  {entry.electorate && entry.electorate}
                  {entry.page && ` • p.${entry.page}`}
                </Typography>
              )
            }
          />
          {hasChildren && (
            <Box onClick={handleToggle} sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
              {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
          )}
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {entry.children.map((child) => (
              <TocEntry
                key={child.id}
                entry={child}
                currentId={currentId}
                loadedIds={loadedIds}
                level={level + 1}
                onSelectFragment={onSelectFragment}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

// #92 Phase 2: per-speech precise ParlView deep-links.
//
// A fragment's `time` is a wall-clock sitting time ("HH:MM:SS"). The video
// player's position 0 is NOT media_som: the VOD file has a pre-roll before
// media_som, so the true seek base is (media_eom - file_duration), which the
// backend computes and returns as `seek_base` (seconds past midnight) (#163).
// The player offset for a moment is therefore (fragment_time - seek_base) in
// seconds, clamped at >= 0. Returns null when the inputs are missing/
// unparseable so the caller falls back to the sitting-level "Watch on ParlView"
// button.
function hmsToSeconds(value) {
  if (!value || typeof value !== 'string') return null;
  // Take the first three colon-separated components (HH:MM:SS); a 4th (frames)
  // in a SMPTE seek base is intentionally ignored.
  const parts = value.trim().split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  const s = parts.length >= 3 ? Number(parts[2]) : 0;
  if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) return null;
  return h * 3600 + m * 60 + s;
}

function computeMomentStartTime(fragmentTime, seekBase) {
  const frag = hmsToSeconds(fragmentTime);
  // seekBase is the backend-computed `seek_base` (seconds past midnight); it is
  // already a number, not an SMPTE string.
  if (frag === null || seekBase === null || !Number.isFinite(seekBase)) {
    return null;
  }
  return Math.max(0, frag - seekBase);
}

function momentDeepLink(watchUrl, startTime) {
  if (!watchUrl || startTime === null) return null;
  // watchUrl is the bare ".../video/{id}" — append the startTime query param.
  const sep = watchUrl.includes('?') ? '&' : '?';
  return `${watchUrl}${sep}startTime=${startTime}`;
}

// #160 (defect 2): the lead "BILLS" fragment's content_html embeds raw APH Hansard
// internal references as relative anchors, e.g.
//   <a href="r7462" type="Bill"><span class="HPS-SubDebate">Some Bill 2026</span></a>
// Inside the SPA those bare hrefs resolve against the current path
// (/hansard/<id>) -> /hansard/r7462 -> getHansard('r7462') -> API 404 ->
// "Failed to load hansard record". They are dead ends, not in-page jumps.
//
// We neutralise just these relative APH refs (href matching ^[rs]\d+$) by removing
// the surrounding <a>...</a> wrapper while keeping the inner content (the bill name
// span) visible and inert. Absolute http(s) links and any other anchors are left
// untouched. Pure + deterministic so it's safe for dangerouslySetInnerHTML.
function sanitizeHansardHtml(html) {
  if (!html || typeof html !== 'string') return html;
  // Match an anchor whose href is a bare APH ref (rNNN / sNNN, no slash/scheme),
  // non-greedily up to its closing </a>, and replace with just its inner content.
  return html.replace(
    /<a\b[^>]*\bhref\s*=\s*(["'])([rs]\d+)\1[^>]*>([\s\S]*?)<\/a>/gi,
    (_match, _quote, _ref, inner) => inner
  );
}

// Component to render a single fragment
function FragmentContent({ fragment, isInitial, parlviewVideo }) {
  if (!fragment) return null;

  // #92 Phase 2: if this fragment has a real time AND the day has a ParlView
  // mapping, offer a precise "Watch this moment" deep-link. Otherwise the
  // sitting-level "Watch on ParlView" button (rendered in the header) is the
  // fallback.
  // #163: offset from `seek_base` (media_eom - file_duration), not media_som.
  // When seek_base is absent (older/missing mapping), we can't compute a precise
  // moment; the per-speech link is suppressed and the sitting-level "Watch on
  // ParlView" button (video start) is the graceful fallback.
  const momentStartTime =
    fragment.time &&
    parlviewVideo &&
    Number.isFinite(parlviewVideo.seek_base)
      ? computeMomentStartTime(fragment.time, parlviewVideo.seek_base)
      : null;
  const momentLink =
    parlviewVideo && parlviewVideo.watch_url && momentStartTime !== null
      ? momentDeepLink(parlviewVideo.watch_url, momentStartTime)
      : null;

  return (
    <Box
      id={`fragment-${fragment.id}`}
      sx={{
        scrollMarginTop: '80px',
        mb: 3,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Speaker info for this fragment */}
      {fragment.speaker_name && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar
            src={fragment.speaker_id ? getParliamentarianPhotoUrl(fragment.speaker_id) : null}
            sx={{ width: 48, height: 48, bgcolor: 'primary.main', flexShrink: 0 }}
          >
            {fragment.speaker_name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {fragment.speaker_id ? (
                <Typography
                  component={RouterLink}
                  to={`/parliamentarians/${fragment.speaker_id}`}
                  variant="subtitle1"
                  sx={{ color: 'ledger.accentText', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                >
                  {fragment.speaker_name}
                </Typography>
              ) : (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{fragment.speaker_name}</Typography>
              )}
              {fragment.party && <Chip label={fragment.party} size="small" variant="outlined" sx={{ height: 20 }} />}
              {fragment.electorate && (
                <Typography variant="body2" color="text.secondary">
                  ({fragment.electorate})
                </Typography>
              )}
              {fragment.time && (
                <Typography variant="caption" color="text.secondary">
                  {fragment.time}
                </Typography>
              )}
              {momentLink && (
                <Link
                  href={momentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  underline="hover"
                  sx={{ color: 'error.main', fontWeight: 600, whiteSpace: 'nowrap' }}
                  title="Open this moment in the sitting's ParlView video (aph.gov.au) in a new tab"
                >
                  ▶ Watch this moment
                </Link>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Procedural / no-speaker fragments: surface the moment link on its own */}
      {!fragment.speaker_name && momentLink && (
        <Box sx={{ mb: 1 }}>
          <Link
            href={momentLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            underline="hover"
            sx={{ color: 'error.main', fontWeight: 600, whiteSpace: 'nowrap' }}
            title="Open this moment in the sitting's ParlView video (aph.gov.au) in a new tab"
          >
            ▶ Watch this moment{fragment.time ? ` (${fragment.time})` : ''}
          </Link>
        </Box>
      )}

      {/* Content */}
      {fragment.content_html ? (
        <Box
          className="hansard-content"
          sx={{
            lineHeight: 1.8,
            '& body': { display: 'block' },
            '& p': { mb: 2, mt: 0 },
            // Debate/SubDebate titles
            '& .HPS-Debate': { fontWeight: 700, fontSize: '1.1rem', color: 'ledger.accentText' },
            '& .HPS-SubDebate': { fontWeight: 600, fontSize: '1rem', color: 'text.primary' },
            '& .HPS-SubSubDebate': { fontWeight: 500, fontSize: '0.95rem', color: 'text.secondary' },
            // Speaker info
            '& .HPS-MemberSpeech': { fontWeight: 600, color: 'ledger.accentText' },
            '& .HPS-MemberContinuation': { fontWeight: 600, color: 'ledger.accentText' },
            '& .HPS-MemberInterjecting': { fontWeight: 600, fontStyle: 'italic', color: 'text.secondary' },
            '& .HPS-Electorate': { fontStyle: 'italic', color: 'text.secondary' },
            '& .HPS-MinisterialTitles': { fontStyle: 'italic', color: 'text.secondary' },
            '& .HPS-Ministry': { fontStyle: 'italic', color: 'text.secondary' },
            '& .HPS-Time': { color: 'text.secondary', fontWeight: 500 },
            // Interjections
            '& .HPS-GeneralIInterjecting, & .HPS-GeneralInterjecting': { fontStyle: 'italic', color: 'text.secondary' },
            // Special formatting
            '& .HPS-Small': { fontSize: '0.9rem', pl: 3, borderLeft: '3px solid', borderLeftColor: 'divider', py: 1, my: 2, display: 'block' },
            '& .HPS-GeneralBold': { fontWeight: 600 },
            '& .HPS-Line': { borderBottom: '1px solid', borderBottomColor: 'divider', my: 2 },
            // Date/procedural
            '& .HPS-SODJobDate': { fontWeight: 600, color: 'text.secondary', fontSize: '0.95rem' },
            '& .HPS-Hansard': { color: 'text.secondary' },
            // Tables
            '& .HPS-TableGrid, & .HPS-TableGrid2': { width: '100%', borderCollapse: 'collapse', my: 2 },
            // Links
            '& a': { color: 'ledger.accentText', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHansardHtml(fragment.content_html) }}
        />
      ) : (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
          {fragment.content}
        </Typography>
      )}
    </Box>
  );
}

export default function HansardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fragments, setFragments] = useState([]); // Array of loaded fragments
  const [initialFragment, setInitialFragment] = useState(null); // The fragment user navigated to
  const [currentId, setCurrentId] = useState(id); // Track which fragment is "current" for TOC highlighting
  const [toc, setToc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tocLoading, setTocLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [parlviewVideo, setParlviewVideo] = useState(null); // #92 Phase 1

  // Refs for infinite scroll
  const topObserverRef = useRef(null);
  const bottomObserverRef = useRef(null);
  const loadedIdsRef = useRef(new Set());
  const contentRef = useRef(null);
  // #160/#161: true until the first successful load. On the very first page visit
  // (cold load / shared deep-link) we leave the browser at its natural scroll
  // position; on every subsequent id change (TOC click or Back/Forward) we scroll
  // the landing fragment into view.
  const isFirstLoadRef = useRef(true);

  // #161: a TOC node click pushes a history entry for that fragment, so the URL is
  // shareable and Back/Forward traverse the click journey. The id-driven load
  // effect then renders that fragment first and (via isFirstLoadRef) scrolls to it,
  // making each click atomically update URL + render + scroll + highlight.
  const selectFragment = useCallback((fragmentId) => {
    if (!fragmentId || fragmentId === id) return;
    navigate(`/hansard/${fragmentId}`);
  }, [navigate, id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Load a fragment by ID
  const loadFragment = useCallback(async (fragmentId) => {
    if (!fragmentId || loadedIdsRef.current.has(fragmentId)) return null;
    try {
      const data = await getHansard(fragmentId);
      loadedIdsRef.current.add(fragmentId);
      return data;
    } catch (err) {
      console.error('Failed to load fragment:', fragmentId, err);
      return null;
    }
  }, []);

  // #160/#161: the in-page multi-fragment loadFragmentGroup was removed. TOC clicks
  // now navigate to the clicked fragment's own URL (see selectFragment above), which
  // the id-driven load effect renders first and scrolls into view — fixing the
  // missing scroll (#160 defect 1) and the off-by-one extra leading fragment
  // (#160 defect 3), and adding shareable URLs + Back/Forward traversal (#161).
  // Neighbouring fragments still stream in via loadMoreFragments (infinite scroll).
  // Load more fragments in a direction (for infinite scroll)
  const loadMoreFragments = useCallback(async (direction) => {
    if (loadingMore || fragments.length === 0) return;

    setLoadingMore(true);
    try {
      if (direction === 'prev') {
        const firstFragment = fragments[0];
        const prevId = getPrevId(firstFragment);
        if (prevId && !loadedIdsRef.current.has(prevId)) {
          const prevFragment = await loadFragment(prevId);
          if (prevFragment) {
            setFragments(prev => [prevFragment, ...prev]);
          }
        }
      } else {
        const lastFragment = fragments[fragments.length - 1];
        const nextId = getNextId(lastFragment);
        if (nextId && !loadedIdsRef.current.has(nextId)) {
          const nextFragment = await loadFragment(nextId);
          if (nextFragment) {
            setFragments(prev => [...prev, nextFragment]);
          }
        }
      }
    } catch (err) {
      console.error('Error loading more:', err);
    }
    setLoadingMore(false);
  }, [fragments, loadingMore, loadFragment]);

  // Initial load
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      loadedIdsRef.current = new Set();
      setFragments([]);
      setParlviewVideo(null);

      try {
        const data = await getHansard(id);
        loadedIdsRef.current.add(id);
        setInitialFragment(data);
        setFragments([data]);
        setCurrentId(id);

        // #160/#161: bring the landing fragment into view on TOC-click / Back-Forward
        // navigations (any id change after the first visit). Defer to the next frame
        // so the fragment Box (#fragment-<id>, scrollMarginTop:80) has mounted. On the
        // very first visit we leave the browser at the top so a cold deep-link doesn't
        // jump unexpectedly.
        if (!isFirstLoadRef.current) {
          const targetId = id;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              document
                .getElementById(`fragment-${targetId}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          });
        }
        isFirstLoadRef.current = false;

        // #92 Phase 1: resolve the day's ParlView video (chamber + date).
        // Returns null when there's no mapping — the link just stays hidden.
        if (data.chamber && data.date) {
          getParlViewVideo(data.chamber, data.date)
            .then((video) => setParlviewVideo(video))
            .catch(() => setParlviewVideo(null));
        }

        // Fetch the TOC record if toc_id is available
        if (data.toc_id) {
          setTocLoading(true);
          try {
            const tocData = await getHansardToc(data.toc_id);
            setToc(tocData);
          } catch (tocErr) {
            console.error('Failed to load TOC:', tocErr);
          }
          setTocLoading(false);
        } else {
          setTocLoading(false);
        }
      } catch (err) {
        console.error('Error fetching hansard:', err);
        setError('Failed to load hansard record');
      }
      setLoading(false);
    };
    fetchData();
  }, [id]);

  // Setup intersection observers for infinite scroll
  useEffect(() => {
    const options = { root: null, rootMargin: '200px', threshold: 0 };

    const topObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore) {
        loadMoreFragments('prev');
      }
    }, options);

    const bottomObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore) {
        loadMoreFragments('next');
      }
    }, options);

    if (topObserverRef.current) topObserver.observe(topObserverRef.current);
    if (bottomObserverRef.current) bottomObserver.observe(bottomObserverRef.current);

    return () => {
      topObserver.disconnect();
      bottomObserver.disconnect();
    };
  }, [loadMoreFragments, loadingMore]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !initialFragment) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Hansard not found'}</Typography>
        <Button component={RouterLink} to="/hansard" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Hansard Search
        </Button>
      </Box>
    );
  }

  const hansard = initialFragment;
  const loadedIds = Array.from(loadedIdsRef.current);

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/hansard"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Hansard Search
        </Button>

        {/* Prev/Next navigation - jumps to that fragment */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {parlviewVideo && parlviewVideo.watch_url && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              component="a"
              href={parlviewVideo.watch_url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OndemandVideoIcon />}
              sx={{ borderRadius: 2 }}
              title="Open this sitting day's video on ParlView (aph.gov.au) in a new tab"
            >
              Watch on ParlView
            </Button>
          )}
          <CopyLinkButton size="small" />
          <ShareButton size="small" title={hansard.debate_title} />
          <DownloadPdfButton size="small" contentId={id} />
          <DownloadXmlButton size="small" contentId={id} />
          <ReportIssueButton size="small" contentType="hansard" contentId={id} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIosNewIcon />}
            onClick={() => getPrevId(hansard) && navigate(`/hansard/${getPrevId(hansard)}`)}
            disabled={!getPrevId(hansard)}
            sx={{ borderRadius: 2 }}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowForwardIosIcon />}
            onClick={() => getNextId(hansard) && navigate(`/hansard/${getNextId(hansard)}`)}
            disabled={!getNextId(hansard)}
            sx={{ borderRadius: 2 }}
          >
            Next
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Table of Contents Sidebar - LEFT */}
        <Grid item xs={12} md={3} sx={{ order: { xs: 2, md: 1 } }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 80,
              maxHeight: 'calc(100vh - 100px)',
              overflow: 'auto'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, fontSize: '0.95rem' }}>
              {hansard.chamber}
              {/* #276 — surface Federation Chamber in the sidebar header so
                  the venue is obvious on a deep-linked speech page. */}
              {(hansard.is_federation_chamber === true || /MAIN COMMITTEE/i.test(hansard.debate_title || '')) && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                  Federation Chamber
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
              {formatDate(hansard.date)}
            </Typography>

            {tocLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : toc && toc.entries ? (
              <List dense disablePadding>
                {toc.entries.map((entry) => (
                  <TocEntry
                    key={entry.id}
                    entry={entry}
                    currentId={currentId}
                    loadedIds={loadedIds}
                    onSelectFragment={selectFragment}
                  />
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No table of contents available.
              </Typography>
            )}

            {toc && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  {toc.total_fragments} items • {fragments.length} shown
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9} sx={{ order: { xs: 1, md: 2 } }}>
          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <RecordVoiceOverIcon sx={{ color: '#c62828', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#c62828' }}>
                {hansard.debate_title}
              </Typography>
            </Box>
            {hansard.subdebate_title && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {hansard.subdebate_title}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={hansard.chamber}
                color={hansard.chamber === 'Senate' ? 'error' : 'primary'}
                size="small"
              />
              {/* #276 — Federation Chamber chip alongside (not replacing) the
                  "House of Reps" chip. Modern docs: is_federation_chamber
                  bool; historic (pre-2012): MAIN COMMITTEE in debate_title. */}
              {(hansard.is_federation_chamber === true || /MAIN COMMITTEE/i.test(hansard.debate_title || '')) && (
                <Chip
                  label="Federation Chamber"
                  size="small"
                  sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 600 }}
                />
              )}
              <Chip label={formatDate(hansard.date)} variant="outlined" size="small" />
              <Chip label={`${hansard.parliament}${hansard.parliament === 1 ? 'st' : hansard.parliament === 2 ? 'nd' : hansard.parliament === 3 ? 'rd' : 'th'} Parliament`} variant="outlined" size="small" />
              <Chip label={`Session ${hansard.session}`} variant="outlined" size="small" />
              {hansard.page && <Chip label={`Page ${hansard.page}`} variant="outlined" size="small" />}
              {hansard.is_proof && <Chip label="Proof" color="warning" size="small" />}
              {hansard.is_first_speech && <Chip label="First Speech" color="success" size="small" />}
            </Box>
          </Paper>

          {/* Speech Content - Continuous scroll */}
          <Paper ref={contentRef} elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            {/* Loading overlay */}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* Top sentinel for loading previous */}
            <Box ref={topObserverRef} sx={{ height: 1 }} />

            {/* Render all loaded fragments */}
            {fragments.map((fragment) => (
              <FragmentContent
                key={fragment.id}
                fragment={fragment}
                isInitial={fragment.id === id}
                parlviewVideo={parlviewVideo}
              />
            ))}

            {/* Bottom sentinel for loading next */}
            <Box ref={bottomObserverRef} sx={{ height: 1 }} />

            {/* End of content indicator */}
            {fragments.length > 0 && !getNextId(fragments[fragments.length - 1]) && (
              <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                <Typography variant="caption">End of content for this section</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
